import "./styles.scss";

import { useEffect, useMemo, useRef } from "react";
import { Formik, Form } from "formik";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";

import ModalNew from "components/ModalNew";
import Calendar from "components/Calendar";
import FormikInput from "components/formik/FormikInput";
import FormikSelect from "components/formik/FormikSelect";

import api from "libs/api";
import website_status_data from "libs/website_status_data.json";
import overviewSchema from "libs/validations/admin/overviewSchema";

import { ReactComponent as GreenStatusIcon } from "assets/icons/green-status.svg";
import { ReactComponent as RedStatusIcon } from "assets/icons/red-status.svg";
import { ReactComponent as BlueStatusIcon } from "assets/icons/blue-status.svg";
import { ReactComponent as OrangeStatusIcon } from "assets/icons/orange-status.svg";

const mainClass = "overview-modal";

const status_icon_render = (status) => {
  switch (status) {
    case 1:
      return <GreenStatusIcon />;
    case 2:
      return <RedStatusIcon />;
    case 3:
      return <BlueStatusIcon />;
    case 4:
      return <OrangeStatusIcon />;
    default:
      return null;
  }
};

const OverviewModal = ({
  data,
  queryDataKey = "client-modal",
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const formikRef = useRef(null);

  const initialValues = useMemo(
    () => ({
      website: data?.website ?? "",
      status: data?.status,
      last_cms_update: data?.last_cms_update,
      domain_valid_until: data?.domain_valid_until,
      password1_link: data?.password1_link ?? "",
    }),
    [data],
  );

  useEffect(() => {
    if (isOpen) {
      formikRef.current?.resetForm({ values: initialValues });
    }
  }, [isOpen, initialValues]);

  const status_options = website_status_data?.map(({ label, value }) => ({
    icon: status_icon_render(value),
    label: t(label),
    value,
  }));

  const onSubmit = async (values, setSubmitting) => {
    try {
      await api.put(`/admin/clients/${data?._id}`, values);

      queryClient.setQueryData([queryDataKey, data?._id], (prev) => ({
        ...prev,
        ...values,
      }));

      onClose();
    } catch (err) {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={overviewSchema}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(values, setSubmitting);
      }}
    >
      {({ isSubmitting, submitForm, values, setFieldValue }) => (
        <ModalNew
          title={`${t("Change the page details")} - ${data?.website}`}
          variant="wide"
          labelSubmit={t("Save")}
          onSubmit={submitForm}
          loading={isSubmitting}
          isOpen={isOpen}
          onClose={onClose}
        >
          <Form autoComplete="off">
            <div className={mainClass}>
              <div className={`${mainClass}__field`}>
                <FormikInput
                  name="website"
                  label={t("Website address")}
                  disabled={isSubmitting}
                  required
                />
              </div>
              <div className={`${mainClass}__field`}>
                <FormikSelect
                  name="status"
                  label={t("Status")}
                  options={status_options}
                  selected={values?.status}
                  disabled={isSubmitting}
                  required
                />
              </div>
              <div className={`${mainClass}__field`}>
                <Calendar
                  anchor="top"
                  label={t("Last CMS update")}
                  default_value={values?.last_cms_update}
                  autoPlacement
                  onChange={(value) => setFieldValue("last_cms_update", value)}
                />
              </div>
              <div className={`${mainClass}__field`}>
                <Calendar
                  anchor="top"
                  label={t("Domain valid until")}
                  default_value={values?.domain_valid_until}
                  autoPlacement
                  onChange={(value) =>
                    setFieldValue("domain_valid_until", value)
                  }
                />
              </div>
              <div className={`${mainClass}__field ${mainClass}__field--full`}>
                <FormikInput
                  name="password1_link"
                  label={t("Access data link")}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </Form>
        </ModalNew>
      )}
    </Formik>
  );
};

export default OverviewModal;
